import { dispatchCartFetch } from '@theme/seal-dispatch-snapshot';
import { CartAddEvent, ThemeEvents } from '@theme/events';

const RESET_DELAY = 1800;

class SealManualComplementary extends HTMLElement {
  connectedCallback() {
    document.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
  }

  handleVariantUpdate = (event) => {
    const productId = event.detail?.data?.productId;
    const html = event.detail?.data?.html;

    if (!html || (productId && this.dataset.productId && productId !== this.dataset.productId)) return;

    const blockId = this.dataset.blockId;
    const sectionId = this.dataset.sectionId;
    if (!blockId || !sectionId) return;

    const next = Array.from(html.querySelectorAll('seal-manual-complementary')).find(
      (candidate) => candidate.dataset.sectionId === sectionId && candidate.dataset.blockId === blockId
    );

    if (!next) return;

    this.innerHTML = next.innerHTML;
    this.toggleAttribute('hidden', next.hasAttribute('hidden'));
    this.dataset.productId = next.dataset.productId || this.dataset.productId || '';

    const list = this.querySelector('.seal-manual-complementary__list');
    if (list) list.scrollTop = 0;
  };
}

class SealManualComplementaryCard extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick = async (event) => {
    const quickviewTrigger = event.target?.closest?.('quick-add-component');
    if (quickviewTrigger && this.contains(quickviewTrigger)) return;

    const clickedButton = event.target?.closest?.('[data-seal-manual-complementary-add]');
    const button = clickedButton && this.contains(clickedButton) ? clickedButton : this.querySelector('[data-seal-manual-complementary-add]');

    if (!button || button.disabled) return;

    event.preventDefault();

    const variantId = button.dataset.variantId || this.dataset.variantId;
    if (!variantId) return;

    this.setButtonState(button, 'loading');

    try {
      const response = await dispatchCartFetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
            },
          ],
          sections: this.getCartSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Cart add failed');
      }

      const cart = await this.fetchCart();

      this.animateFlyToCart();
      this.setButtonState(button, 'added');

      window.setTimeout(() => {
        this.dispatchEvent(
          new CartAddEvent(cart ?? data, `seal-manual-complementary-${variantId}`, {
            source: cart ? 'seal-manual-complementary' : 'product-form-component',
            itemCount: cart?.item_count ?? 1,
            variantId,
            sections: data.sections,
            openCartDrawer: false,
          })
        );
      }, 180);

      window.setTimeout(() => {
        this.setButtonState(button, 'idle');
      }, RESET_DELAY);
    } catch (error) {
      console.error(error);
      this.setButtonState(button, 'error');

      window.setTimeout(() => {
        this.setButtonState(button, 'idle');
      }, RESET_DELAY);
    }
  };

  getCartSectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js', {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) return null;

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      return null;
    }
  }

  animateFlyToCart() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const source = this.querySelector('.seal-manual-complementary__image');
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (!source || !cartIcon || !customElements.get('fly-to-cart')) return;

    const imageUrl = source.currentSrc || source.src;
    if (!imageUrl) return;

    const flyToCartElement = document.createElement('fly-to-cart');

    flyToCartElement.classList.add('fly-to-cart--quick');
    flyToCartElement.style.setProperty('background-image', `url(${imageUrl})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = source;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  setButtonState(button, state) {
    const liveLabel = button.querySelector('.visually-hidden');
    const defaultLabel = button.dataset.label || '';
    const addedLabel = button.dataset.addedLabel || defaultLabel;
    const errorLabel = button.dataset.errorLabel || defaultLabel;

    button.classList.toggle('is-loading', state === 'loading');
    button.classList.toggle('is-added', state === 'added');
    button.classList.toggle('is-error', state === 'error');
    button.disabled = state === 'loading' || state === 'added';

    if (!liveLabel) return;

    if (state === 'added') {
      liveLabel.textContent = addedLabel;
    } else if (state === 'error') {
      liveLabel.textContent = errorLabel;
    } else {
      liveLabel.textContent = defaultLabel;
    }
  }
}

if (!customElements.get('seal-manual-complementary')) {
  customElements.define('seal-manual-complementary', SealManualComplementary);
}

if (!customElements.get('seal-manual-complementary-card')) {
  customElements.define('seal-manual-complementary-card', SealManualComplementaryCard);
}
